import React, {useEffect, useRef, useState} from "react";
import {Button, Card, Col, DatePicker, Row, Spin, Table, Tag, Typography} from "antd";
import dayjs from "dayjs";
import PropTypes from "prop-types";

import EngageCurrency from "./EngageCurrency";
import CostBreakdown from "./CostBreakdown";
import {getActiveAccount} from "../../lib/activeMember";
import {messageType, showMessages} from "../../helpers/message";
import {getTransactions, getTransactionType} from "../../helpers/billing";
import {ucFirst} from "../../helpers/misc";
import {dateFormat} from "../../lib/time";
import {emptyTransactionHistoryText} from "../../helpers/text";

import {black, successGreen} from "../../assets/colors";
import "./TransactionHistory.less";
import {userHasRole, userIsStaff} from '../../lib/role';

const {RangePicker} = DatePicker;
const {Text} = Typography;

const columnDriverValues = [
    {
        title: <Typography.Text strong>Date</Typography.Text>,
        key: 'date',
        render: transaction =>
            <span className="date-line-item long-text-wrap" >
                {dayjs(transaction.transacted_at).tz().format(dateFormat)}
            </span>
    },
    {
        title: <Typography.Text strong>Transaction Type</Typography.Text>,
        key: 'type',
        render: transaction =>
            <span className="type-line-item long-text-wrap" >
                {getTransactionType(transaction)}
            </span>

    },
    {
        title: <Typography.Text strong>Amount</Typography.Text>,
        key: 'amount',
        render: transaction =>
           <span className="amount-line-item long-text-wrap">
               <EngageCurrency
                   amount={parseFloat(transaction.amount)}
                   style={{color: transaction.amount > 0 ? black : successGreen}}
               />
           </span>
    },
    {
        title: <Typography.Text strong>Status</Typography.Text>,
        dataIndex: 'state',
        key: 'state',
        render: state => {
            let colour;
            switch (state) {
                case 'pending':
                    colour = 'gold'
                    break;
                case 'reconciled':
                    colour = 'geekblue'
                    break;
                case 'invoiced':
                    colour = 'green'
                    break;
                default:
                    return null;
            }
            return (
            <>
                <span className="status-line-item">Status: </span>
                <Tag color={colour} key={state}>{ucFirst(state)}</Tag>
            </>
            )
        },
    },
    {
        title: <Typography.Text strong>Due Date</Typography.Text>,
        key: 'due_date',
        render: transaction => {
            let dueDate = 'N/A';
            if (transaction.due_date) {
                dueDate = dayjs(transaction.due_date).tz().format(dateFormat);
            }
            return <span className="due-date-line-item long-text-wrap">{dueDate}</span>
        },
    },
];
const columnOwnerValues = [
    ...columnDriverValues,
    {
        title: <Typography.Text strong>Incurred Driver</Typography.Text>,
        key: 'incurred_driver',
        render: transaction => {
            let incurredDriver = 'N/A';
            if (transaction.driver_name) {
                incurredDriver = transaction.driver_name;
            }
            return <span className="incurred-driver-line-item">{incurredDriver}</span>
        },
    },
];

const TransactionHistory = ({refreshBalances, toggleRefreshBalances}) => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);

    const [startedAt, setStartedAt] = useState(null);
    const [endedAt, setEndedAt] = useState(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [total, setTotal] = useState(10);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [columns, setColumns] = useState(columnDriverValues);

    useEffect(() => {
        if (loading === true) {
            fetchTransactions(page, perPage, startedAt, endedAt);
        }
    }, [page, perPage, startedAt, endedAt, loading]);

    useEffect(() => {
        if (refreshBalances === true) {
            fetchTransactions(page, perPage, startedAt, endedAt);
            toggleRefreshBalances(false);
        }
    }, [page, perPage, startedAt, endedAt, refreshBalances, toggleRefreshBalances]);

    useEffect(() => {
        const abortController = abortControllerRef.current;
        return function abort() {
            abortController.abort();
        }
    }, []);

    const fetchTransactions = async (page, perPage, startedAt, endedAt) => {
        setLoading(true);

        try {
            if (userIsStaff() || userHasRole('user', 'owner')) {
                setColumns(columnOwnerValues);
            } else {
                setColumns(columnDriverValues);
            }

            const account = getActiveAccount();
            if (account === null) {
                showMessages('No Account attached!', messageType.error);
                setLoading(false);
                return;
            }
            const accountID = account.id;
            const abortSignal = abortSignalRef.current;

            const transactions = await getTransactions(
                accountID,
                {page, perPage, startedAt, endedAt},
                abortSignal
            );

            if (abortSignal.aborted === false) {
                if (transactions.length !== 0) {
                    setTransactions(transactions.data);
                    setPage(transactions.meta.current_page);
                    setPerPage(transactions.meta.per_page);
                    setTotal(transactions.meta.total);
                }
                setLoading(false);
            }

        } catch (e) {
            showMessages(e.message, messageType.error);
            setLoading(false);
            if(e.status_code === 404) window.location.replace('/404');
        }
    }

    const resetTransactionRange = () => {
        setStartedAt(null);
        setEndedAt(null);
        fetchTransactions(page, perPage, null, null);
    }

    const filterTransactions = () => {
        fetchTransactions(page, perPage, startedAt, endedAt);
    }

    const onDateFilterChange = (values) => {
        if (values !== null) {
            setStartedAt(values[0]);
            setEndedAt(values[1]);
        } else {
            setStartedAt(null);
            setEndedAt(null);
        }
    }

    const onChange = (page, perPage) => {
        setPage(page);
        setPerPage(perPage);
        fetchTransactions(page, perPage, startedAt, endedAt);
    }

    const pagination = {
        current: page,
        pageSize: perPage,
        total,
        onChange,
        showSizeChanger: true,
        simple: window.innerWidth <= 767,
        onShowSizeChange: onChange,
    };

    return (
        <>
            <Card bordered={true}>
                <Row>
                    <Col xs={24} md={16}>
                        <Text>Date filter:</Text>
                        <RangePicker
                            className="date-filter"
                            value={[startedAt, endedAt]}
                            format={dateFormat}
                            presets={{
                                Today: [dayjs().tz(), dayjs().tz()]
                            }}
                            onChange={onDateFilterChange}
                        />
                    </Col>
                    <Col xs={24} md={8} className="date-filter-button-wrapper">
                        <Button className="reset-btn" onClick={resetTransactionRange}>Reset</Button>
                        <Button type="primary" onClick={filterTransactions}>Filter</Button>
                    </Col>
                </Row>
            </Card>
            <br/>
            <Spin spinning={loading}>
                <Table
                    rowKey="id"
                    dataSource={transactions}
                    columns={columns}
                    bordered={false}
                    title={() => <Typography.Text>Transaction History</Typography.Text>}
                    className="responsive-table expandable-table transaction-history"
                    expandable={{
                        expandedRowRender: transaction => <CostBreakdown transaction={transaction}/>,
                        expandRowByClick: true,
                    }}
                    pagination={pagination}
                    locale={{emptyText: (<div className="empty-data-table">{emptyTransactionHistoryText}</div>)}}
                />
            </Spin>
        </>
    );
}

TransactionHistory.propTypes = {
    refreshBalances: PropTypes.bool.isRequired,
    toggleRefreshBalances: PropTypes.func.isRequired,
}

export default TransactionHistory;
